<template>
  <button
    :class="['halloween-expedition-nav-button', `gradient-${props.hoverGradient || 'blue'}`]"
    :disabled="props.disabled"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="onClick"
  >
    <span ref="iconRef" class="halloween-expedition-nav-button-icon">
      <img :src="props.icon" alt="Navigation button icon" />
    </span>
    <span
      v-if="isShowBadge"
      :class="['halloween-expedition-nav-button-badge', { negative: props.badge && props.badge < 0 }]"
    >
      {{ props.badge }}
    </span>
    <span
      v-if="$slots.default"
      :class="['halloween-expedition-nav-button-label', { show: showLabel || props.disabled }]"
    >
      <slot />
    </span>
  </button>
</template>
<script lang="ts" setup>
import { gsap } from 'gsap';

const props = defineProps<{
  badge?: number;
  icon: string;
  hoverGradient?: 'blue' | 'green' | 'red';
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'click', ev: MouseEvent): void;
}>();

function onClick(ev: MouseEvent) {
  if (props.disabled) return;
  emit('click', ev);
}

const isShowBadge = computed(() => props.badge && Math.abs(props.badge) > 0.01);
const iconRef = ref<HTMLSpanElement | null>(null);
const showLabel = ref(false);

let tl: gsap.core.Tween | null = null;

const handleMouseEnter = () => {
  if (props.disabled) return;
  showLabel.value = true;
  tl?.kill();
  tl = gsap.fromTo(
    iconRef.value,
    { scale: 1 },
    {
      scale: 1.15,
      duration: 0.3,
      top: -10,
      ease: 'bounce.out'
    }
  );
};

const handleMouseLeave = () => {
  tl?.kill();
  showLabel.value = false;
  gsap.to(iconRef.value, { scale: 1, duration: 0.3, top: 0, ease: 'bounce.out' });
};
</script>
<style scoped>
.halloween-expedition-nav-button {
  background: radial-gradient(92.3% 92.29% at 50% -7.05%, rgba(246, 242, 103, 0.5) 0%, rgba(0, 0, 0, 0) 100%),
    radial-gradient(59.02% 71.08% at 84.01% 85.82%, rgba(31, 96, 126, 0.5) 0%, rgba(8, 27, 37, 0.5) 100%);
  border: 2px solid #608897;
  box-shadow: 2px 3px 4px rgba(0, 0, 0, 0.25);

  position: relative;
  display: grid;
  place-items: center;
  width: 86px;
  height: 86px;
  min-width: 86px;
  border-radius: 100%;
  cursor: pointer;

  transition: transform 0.2s;
  backdrop-filter: blur(30px);

  &::before {
    content: '';

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 100%;

    background: linear-gradient(180deg, #05a6e0 0%, #051117 80.23%),
      radial-gradient(41.74% 443.57% at 45.85% 130%, #040f14 0%, #081b25 100%);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.7);
    pointer-events: none;
    opacity: 0;

    transition: opacity 0.2s;
  }

  &:not([disabled]):hover::before {
    opacity: 1;
  }

  &.gradient-green::before {
    background: linear-gradient(180deg, #00d42c 0%, #003708 80.23%),
      radial-gradient(41.74% 443.57% at 45.85% 130%, #040f14 0%, #081b25 100%);
  }

  &.gradient-red::before {
    background: linear-gradient(180deg, #d30600 0%, #5b0000 80.23%),
      radial-gradient(41.74% 443.57% at 45.85% 130%, #040f14 0%, #081b25 100%);
  }

  &:not([disabled]):active {
    transform: scale(0.95);
  }

  &[disabled] {
    filter: grayscale(100%);
    cursor: not-allowed;
  }

  @media screen and (max-width: 640px) {
    width: 66px;
    height: 66px;
    min-width: 66px;
  }
}
.halloween-expedition-nav-button-icon {
  width: 100%;
  height: 100%;
  position: relative;
  display: grid;
  place-items: center;
  img {
    position: relative;
    width: 100%;
    height: 100%;
  }
}

.halloween-expedition-nav-button-label {
  position: absolute;
  width: fit-content;
  margin: 0 auto;

  background: rgba(0, 0, 0, 0.8);
  border: 2px solid #608897;
  border-radius: 15px;
  bottom: -10px;
  padding: 2px 10px;
  line-height: 21px;

  font-size: 20px;
  font-weight: 600;
  color: #abc3cf;
  pointer-events: none;
  opacity: 0;

  transition: opacity 0.2s;
  &.show {
    opacity: 1;
  }

  @media screen and (max-width: 640px) {
    font-size: 14px;
    padding: 2px 6px;
  }
}

.halloween-expedition-nav-button[disabled] .halloween-expedition-nav-button-label {
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #505050;
  border-radius: 15px;

  font-size: 14px;
  font-weight: 600;
  color: #4b4b4b;
}

.halloween-expedition-nav-button-badge {
  position: absolute;
  top: -5px;
  left: -5px;
  width: 28px;
  height: 28px;
  background: #081b25;
  border-radius: 100%;
  display: grid;
  place-items: center;
  font-size: 14px;

  border: 2px solid #49ddaa;
  color: #49ddaa;

  font-family:
    PT Serif,
    serif;
  font-weight: 700;
  line-height: 100%;
  text-align: center;
  vertical-align: middle;

  &.negative {
    color: #f6164c;
    border-color: #f6164c;
  }
}
</style>
